// src/App.js
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom"
import Header from "./components/Header"
import ProtectedRoute from "./components/ProtectedRoute"
import UserDashboard from "./pages/UserDashboard"
import TranscriptionsPage from "./pages/TranscriptionsPage"
import { isLoggedIn } from "./auth"
import { LanguageProvider } from "./context/LanguageContext"

// Core pages
import LandingPage from "./pages/LandingPage"
import LoginPage from "./pages/LoginPage"
import RegisterPage from "./pages/RegisterPage"
import ForgotPasswordPage from "./pages/ForgotPasswordPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import AdminPage from "./pages/AdminPage"
import HelpPage from "./pages/HelpPage"
import AnalyticsPage from "./pages/AnalyticsPage"
// import PricingPage from "./pages/PricingPage" // Removed
import TermsPage from "./pages/TermsPage"
import PrivacyPage from "./pages/PrivacyPage"
import TranscriptionService from "./pages/TranscriptionService"
import AnalysisHistoryPage from "./pages/AnalysisHistoryPage"
import CallToolsDashboard from "./pages/CallToolsDashboard"

// Updated analyzer pages
import AnalyzerLandingPage from "./pages/AnalyzerLandingPage"
import HomeImprovementAnalyzer from "./pages/analyzers/HomeImprovementAnalyzer"
import SolarEnergyAnalyzer from "./pages/analyzers/SolarEnergyAnalyzer"
import InsuranceAnalyzer from "./pages/analyzers/InsuranceAnalyzer"

// Component to redirect logged-in users away from auth pages
const AuthRedirect = ({ children }) => {
  if (isLoggedIn()) {
    return <Navigate to="/analyzer" replace />
  }
  return children
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <div className="App">
          <Header />
          <Routes>
          <Route path="/" element={<LandingPage />} />
          
          {/* Auth pages with redirect if already logged in */}
          <Route path="/login" element={
            <AuthRedirect>
              <LoginPage />
            </AuthRedirect>
          } />
          <Route path="/register" element={
            <AuthRedirect>
              <RegisterPage />
            </AuthRedirect>
          } />
          <Route path="/forgot-password" element={
            <AuthRedirect>
              <ForgotPasswordPage />
            </AuthRedirect>
          } />
          <Route path="/reset-password" element={
            <AuthRedirect>
              <ResetPasswordPage />
            </AuthRedirect>
          } />
          
          {/* Public pages */}
          <Route path="/pricing" element={<Navigate to="/login" replace />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Protected analyzer routes */}
          <Route
            path="/analyzer"
            element={<Navigate to="/analyzer/home-improvement" replace />}
          />

          {/* Individual Analyzer Routes */}
          <Route
            path="/analyzer/home-improvement"
            element={
              <ProtectedRoute>
                <HomeImprovementAnalyzer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analyzer/solar-energy"
            element={
              <ProtectedRoute>
                <SolarEnergyAnalyzer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analyzer/insurance"
            element={
              <ProtectedRoute>
                <InsuranceAnalyzer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcribe"
            element={
              <ProtectedRoute>
                <TranscriptionService />
              </ProtectedRoute>
            }
          />

          {/* Placeholder routes for future analyzers */}
          <Route
            path="/analyzer/law-offices"
            element={
              <ProtectedRoute>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Law Offices Analyzer</h1>
                    <p className="text-gray-600">Coming Q2 2025</p>
                    <Link to="/analyzer" className="text-emerald-600 hover:underline mt-4 inline-block">
                      ← Back to Analyzers
                    </Link>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analyzer/medical-office"
            element={
              <ProtectedRoute>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Medical Office Analyzer</h1>
                    <p className="text-gray-600">Coming Q2 2025</p>
                    <Link to="/analyzer" className="text-emerald-600 hover:underline mt-4 inline-block">
                      ← Back to Analyzers
                    </Link>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analyzer/general-customer-service"
            element={
              <ProtectedRoute>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">General Customer Service Analyzer</h1>
                    <p className="text-gray-600">Coming Q3 2025</p>
                    <Link to="/analyzer" className="text-emerald-600 hover:underline mt-4 inline-block">
                      ← Back to Analyzers
                    </Link>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />

          {/* Admin & other protected routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis-history"
            element={
              <ProtectedRoute>
                <AnalysisHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transcriptions"
            element={
              <ProtectedRoute>
                <TranscriptionsPage />
              </ProtectedRoute>
            }
          />  
          <Route
            path="/calltools"
            element={
              <ProtectedRoute>
                <CallToolsDashboard />
              </ProtectedRoute>
            }
          />
          </Routes>
        </div>
      </Router>
    </LanguageProvider>
  )
}

export default App
